import {
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLL_STRATEGY,
  VirtualScrollStrategy,
} from '@angular/cdk/scrolling';
import {
  ContentChild,
  Directive,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  Optional,
  SkipSelf,
} from '@angular/core';
import { Observable, Subject, SubscriptionLike } from 'rxjs';
import { delay, distinctUntilChanged } from 'rxjs/operators';

const VIRTUAL_SCROLL_START_BUFFER = 100;
const VIRTUAL_SCROLL_END_BUFFER = 150;

// FOR DEBUGGING:
// const VIRTUAL_SCROLL_START_BUFFER = -200;
// const VIRTUAL_SCROLL_END_BUFFER = -150;

export interface VirtualForConfig<T> {
  itemSize: number | ((data?: T) => number);
  visibleRows?: (rows: T[]) => void;
  refresh$?: Observable<void>;
}

@Directive({
  selector: 'cdk-virtual-scroll-viewport[config]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: _getVirtualScrollStrategyFactory,
      deps: [
        forwardRef(() => VirtualConfigDirective),
        [new SkipSelf(), new Optional(), VIRTUAL_SCROLL_STRATEGY],
      ],
    },
  ],
  standalone: false,
})
export class VirtualConfigDirective<T> implements OnChanges {
  @Input() config: VirtualForConfig<T>;

  private _virtualForOf: CdkVirtualForOf<T>;
  @ContentChild(CdkVirtualForOf) set virtualForOf(value: CdkVirtualForOf<T>) {
    if (this._virtualForOf !== value) {
      this._virtualForOf = value;
      this._scrollStrategy.attachVirtualForOf(value);
    }
  }

  @HostBinding('style.--virtual-offset.px') get contentOffset() {
    return this._scrollStrategy.contentOffset;
  }

  // We use this to allocate the correct space prior to the viewport initialising
  @HostBinding('style.--virtual-size.px') get contentSize() {
    return this._scrollStrategy.contentSize;
  }

  readonly _scrollStrategy = new VirtualScrollConfigStrategy<T>();

  ngOnChanges(): void {
    this._scrollStrategy.updateConfig(this.config);
  }
}

export function _getVirtualScrollStrategyFactory<T>(getItemSizeDir: VirtualConfigDirective<T>) {
  return getItemSizeDir._scrollStrategy;
}

export class VirtualScrollConfigStrategy<T> implements VirtualScrollStrategy {
  private readonly _scrolledIndexChange = new Subject<number>();

  scrolledIndexChange: Observable<number> = this._scrolledIndexChange.pipe(distinctUntilChanged());

  contentSize = 0;
  contentOffset = 0;

  private _config: VirtualForConfig<T> | null = null;
  private _viewport: CdkVirtualScrollViewport | null = null;
  private _virtualForOf: CdkVirtualForOf<T> | null = null;

  private _items: readonly T[] | null = null;

  private _subscriptions: SubscriptionLike[] = [];

  updateConfig(config: VirtualForConfig<T>) {
    this._config = config;

    if (this._config.refresh$) {
      this._subscriptions.push(
        this._config.refresh$.pipe(delay(1)).subscribe(() => {
          this._updateTotalContentSize();
          this._updateRenderedRange();
        }),
      );
    }

    this._updateTotalContentSize();
    this._updateRenderedRange();
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this._viewport = viewport;

    this._updateTotalContentSize(/* setIfUnchanged: */ true);
    this._updateRenderedRange();
  }

  attachVirtualForOf(virtualForOf: CdkVirtualForOf<T>): void {
    this._virtualForOf = virtualForOf;

    this._subscriptions.push(
      this._virtualForOf.dataStream.subscribe(items => {
        this._items = items;
        this._updateTotalContentSize();
        // DO NOT UPDATE RENDERED RANGE
        // Sometimes this happens as a change in identity
        // Updating rendering range causes strange scroll issues
      }),
    );
  }

  detach(): void {
    this._scrolledIndexChange.complete();
    this.contentSize = 0;
    this.contentOffset = 0;
    this._config = null;
    this._viewport = null;
    this._virtualForOf = null;
    this._items = null;
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  onContentScrolled(): void {
    this._updateRenderedRange();
  }

  onDataLengthChanged(): void {
    this._updateTotalContentSize();
    this._updateRenderedRange();
  }

  onContentRendered(): void {
    // Do nothing
  }

  onRenderedOffsetChanged(): void {
    // Do nothing
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this._viewport || !this._items || !this._config) {
      return;
    }

    const itemSize = this._config.itemSize;
    const offset =
      typeof itemSize === 'number'
        ? index * itemSize
        : this._items.slice(0, index).reduce((a, x) => a + itemSize(x), 0);
    this._viewport.scrollToOffset(offset, behavior);
  }

  private _updateTotalContentSize(setIfUnchanged = false) {
    /*
     * Content Size is used in the VirtualConfigDirective so calculate
     * even if Viewport isn't attached
     */

    if (!this._items || !this._config) {
      return;
    }

    const itemSize = this._config.itemSize;
    const contentSize =
      typeof itemSize === 'number'
        ? this._items.length * itemSize
        : this._items.reduce((a, x) => a + itemSize(x), 0);

    if (!setIfUnchanged && this.contentSize === contentSize) {
      return;
    }

    this.contentSize = contentSize;

    this._viewport?.setTotalContentSize(contentSize);
  }

  private _updateRenderedRange() {
    if (!this._viewport || !this._items || !this._config) {
      return;
    }

    const { itemSize, visibleRows } = this._config;

    const renderedRange = this._viewport.getRenderedRange();
    const newRange = { start: this._items.length, end: this._items.length };

    let contentOffset = 0;

    const viewportSize = this._viewport.getViewportSize();
    const scrollOffset = this._viewport.measureScrollOffset();
    const visibleStartOffset = scrollOffset - VIRTUAL_SCROLL_START_BUFFER;
    const visibleEndOffset = scrollOffset + viewportSize + VIRTUAL_SCROLL_END_BUFFER;

    if (typeof itemSize === 'number') {
      newRange.start = itemSize > 0 ? Math.max(0, Math.floor(visibleStartOffset / itemSize)) : 0;
      newRange.end = itemSize > 0 ? Math.max(0, Math.ceil(visibleEndOffset / itemSize)) : 0;
      if (newRange.start >= this._items.length) {
        newRange.start = 0;
        newRange.end = 0;
      } else {
        if (newRange.end > this._items.length) {
          newRange.end = this._items.length;
        }
        contentOffset = newRange.start * itemSize;
      }
    } else {
      if (visibleStartOffset <= 0) {
        newRange.start = 0;
      }

      let currentOffset = 0;
      for (let i = 0; i < this._items.length; i++) {
        const currentSize = itemSize(this._items[i]);

        if (currentOffset < visibleStartOffset) {
          currentOffset += currentSize;

          if (currentOffset >= visibleStartOffset) {
            newRange.start = i;
            contentOffset = currentOffset - currentSize;
          }
        } else {
          currentOffset += currentSize;

          if (currentOffset >= visibleEndOffset) {
            newRange.end = i + 1;
            break;
          }
        }
      }
    }

    if (newRange.start >= newRange.end) {
      newRange.start = 0;
      newRange.end = 0;
    }

    if (renderedRange.start === newRange.start && renderedRange.end === newRange.end) {
      return;
    }

    this._viewport.setRenderedRange(newRange);
    this._viewport.setRenderedContentOffset(contentOffset);
    this._scrolledIndexChange.next(newRange.start);
    this.contentOffset = contentOffset;

    visibleRows?.(this._items.slice(newRange.start, newRange.end));
  }
}
